// Node contract for the composable TexAvatars inference graph (DAG of camera/encoder/decoder/branch/lift nodes).

import type * as ort from "../inference/ort";
import type { LiftInput } from "../inference/lift";
import type * as THREE from "three";

// The two mutable per-frame drivers; a node's dirtyOn() picks the minimum recompute set.
export type DirtySource = "rig" | "camera";

// A graph-edge value: CPU Float32Array or a live gpu-buffer ORT tensor on the shared device.
export type NodeTensor =
  | { readonly loc: "cpu"; readonly data: Float32Array; readonly dims: readonly number[] }
  | { readonly loc: "gpu-buffer"; readonly tensor: ort.Tensor; readonly dims: readonly number[] };

export const isGpuTensor = (v: NodeTensor): boolean => v.loc === "gpu-buffer";

// Adapt a NodeTensor to the lifter's LiftInput union.
export function nodeTensorToLiftInput(v: NodeTensor): LiftInput {
  if (v.loc === "cpu") return v.data;
  const n = v.dims.reduce((a, b) => a * b, 1);
  // The element size follows the TENSOR's dtype, not an assumption: a decoder
  // exported with an fp16 output (tools/ogs_decoder_output_layout.py --fp16-out)
  // hands back half as many bytes, and reporting the fp32 count made the lift
  // decode fp16 pairs as fp32 — a head that renders as coloured noise.
  const t = v.tensor as unknown as { gpuBuffer: GPUBuffer; type?: string };
  const bytesPerElement = t.type === "float16" ? 2 : 4;
  return { gpuBuffer: t.gpuBuffer, byteLength: n * bytesPerElement };
}

// Inverse adapter: wrap a runner-path LiftInput as a graph-edge NodeTensor (dims must match its byteLength).
export function liftInputToNodeTensor(v: LiftInput, dims: readonly number[]): NodeTensor {
  if (v instanceof Float32Array) return { loc: "cpu", data: v, dims };
  return { loc: "gpu-buffer", tensor: { gpuBuffer: v.gpuBuffer } as unknown as ort.Tensor, dims };
}

export interface PortSpec {
  readonly name: string;
}

// `from` is an upstream node id, or the reserved sources "__rig__" / "__camera__" (bound to FrameInputs).
export interface EdgeBinding {
  readonly from: string;
  readonly fromPort: string;
  readonly toPort: string;
}

export interface FrameInputs {
  readonly rig: Float32Array | null;
  readonly camera: THREE.PerspectiveCamera;
  readonly rigEpoch: number;
  readonly cameraEpoch: number;
}

export interface NodeContext {
  readonly ort: typeof ort;
  readonly uvRes: number;
  /** Resolution the decoders emit at and the plücker must be generated at; uvRes when absent. */
  readonly decoderRes?: number;
  readonly rigDim: number;
  /** THE PAGE'S ONE DEVICE (`sharedGpuDevice.js`) — the renderer's, ORT's and the
   *  lift's — or null when there is no WebGPU at all.
   *
   *  It was `sharedDevice`, which was exactly `gpuBufferIo ? device : null`: one
   *  field answering two questions. So a node that wanted only "is there a device"
   *  was told "no" whenever the IO was off, and `?gpubuf=0` turned off the GPU
   *  plücker and refused the splat overlay as well as the thing it names. Ask for
   *  the device here and for the IO next door. */
  readonly device: GPUDevice | null;
  /** Do decoder outputs stay ON that device (`ort.Tensor.fromGpuBuffer`), or come
   *  back through the CPU. Independent of `device`: `?gpubuf=0` and a demoted
   *  session both land here and neither moves the lift off the GPU. */
  readonly gpuBufferIo: boolean;
}

// Per-node asset bytes/urls resolved by the loader, keyed by a node-local name ("fp32"/"fp16"/"latent").
export interface NodeAssets {
  readonly [key: string]: ArrayBuffer | Uint8Array | string | undefined;
}

// Stateful, long-lived compute node; owns its session/GPU resources. The runtime calls evaluate only when dirty.
export interface Node {
  readonly id: string;
  readonly type: string;
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[];
  readonly bindings: readonly EdgeBinding[];

  init(ctx: NodeContext, assets?: NodeAssets): Promise<void>;

  // `inbound` holds upstream outputs keyed by THIS node's input-port name; returns outputs keyed by output-port name.
  evaluate(
    frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>>;

  dirtyOn(): readonly DirtySource[];

  dispose(): void;
}
